<template>
  <div>
    <p>
      RI DMV takes online security very seriously and authenticates every user, where possible, with Multi Factor Authentication (MFA).
      We do not seem to have a correct email and / or cell phone # on file for you to complete MFA electronically. To access your Online Customer Account profile you have the following options. Select one of the options and click 'Next'.
    </p>
    <div class="input-row">
      <input type="radio" class="otp" id="otp-mail" name="otherDetails" v-model="mfaType" value="3" @change="errorMsg = ''">
      <label class="otp-text" for="otp-mail">
        <p><strong>Option 1 Send login code by mail:</strong> You can choose to receive a login code by mail. Once you receive the login code, please re-login on the online DMV Customer portal and you
          will be prompted to enter the login code. On successful validation of the login code you will be able to setup email and / or cell phone # on your record with RI DMV.<br />
          Please confirm if the following mailing address we have on file for you is correct. If this is correct, we will mail you a login code that will be valid for 45 days.</p>
        <span class="user-mail-address-heading">Mailing address on file</span>
        <div class="user-address" v-if="contactInfo.mailingAddress">
          <span>{{ contactInfo.mailingAddress.addressLine1 }} </span>
          <span>{{ contactInfo.mailingAddress.addressLine2 }}</span>
          <span>
            {{ contactInfo.mailingAddress.city }},
            {{ contactInfo.mailingAddress.addressType === 'Us Domestic' ? contactInfo.mailingAddress.state : contactInfo.mailingAddress.province }},
            {{ contactInfo.mailingAddress.country }}
          </span>
          <span>
            {{ contactInfo.mailingAddress.addressType === 'Us Domestic' ?
              contactInfo.mailingAddress.zipPlus == '' ? contactInfo.mailingAddress.zipCode :
              `${contactInfo.mailingAddress.zipCode} - ${contactInfo.mailingAddress.zipPlus}` :
              `${contactInfo.mailingAddress.internationalZipCode}`
            }}
          </span>
        </div>
      </label>
    </div>
    <div class="input-row">
      <input type="radio" class="otp" id="otp-visit" name="otherDetails" value="DMV-visit" v-model="mfaType" @change="errorMsg = ''">
      <label class="otp-text" for="otp-visit">
        <p><strong>Option 2 Update Contact info on next DMV visit:</strong> You will not be able to access your Online Customer Account profile until you can be verified with Multi Factor Authentication (MFA).</p>
      </label>
    </div>
    <div class="input-row">
      <input type="radio" class="otp" id="otp-unable" name="otherDetails" value="MFA-unable" v-model="mfaType" @change="errorMsg = ''">
      <label class="otp-text" for="otp-unable">
        <p><strong>Option 3 Not able to receive MFA code:</strong> You can perform a single transaction.</p>
      </label>
    </div>
    <p v-if="errorMsg" class="error-msg">
      {{ errorMsg }}
    </p>
    <p class="sr-only" v-if="errorMsg">
      {{ errorMsg }}
    </p>
    <BaseButtonGroup>
      <button type="button" @click="handleRouting('previous')">Previous</button>
      <CustomButton type="submit" @click="handleRouting('next')">Next</CustomButton>
    </BaseButtonGroup>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue';
import { useStore } from 'vuex';
import { useRouter } from 'vue-router';
import BaseButtonGroup from '@/components/ui/BaseButtonGroup.vue';
import * as COMMONS from '../../store/_commons';
import CustomButton from '../CustomButton.vue';

const store = useStore();
const router = useRouter();

const mfaType = ref('');
const errorMsg = ref('');

// Computed properties
const contactInfo = computed(() => store.state.guestLogin.loginUserDetails || {});

const getGuestLoginMfaRequest = (payload) => store.dispatch(COMMONS.GUEST_LOGIN_GET_LOGIN_MFA_REQUEST, payload);

// Lifecycle hooks 
onMounted(() => {
  window.scroll(0, 0);
  document.body.setAttribute('tabindex', '1');
  document.body.focus();
  document.body.removeAttribute('tabindex');
  document.getElementById('app').scrollTop = 0;
});

// Methods 
const handleRouting = async (info) => {
  if (info === 'previous') {
    handlePreviousRouting();
    return;
  }

  handleNextRouting();
};

const handlePreviousRouting = () => {
  const hasEmailOrPhone = contactInfo.value?.email !== '' || contactInfo.value?.phone !== '';
  const paperOtpSent = contactInfo.value?.isPaperOtpSent;

  if (hasEmailOrPhone) {
    router.push({ name: 'MultiFactAuth', query: { step: '1' } });
    return;
  }

  if (paperOtpSent) {
    router.push({ name: 'MultiFactAuth', query: { step: '2' } });
    return;
  }

  router.push({ name: 'GuestSignin' });
};

const handleNextRouting = async () => {
  const selectedMfa = mfaType.value;

  if (!selectedMfa) {
    errorMsg.value = 'Please select one of the options above';
    return;
  }

  if (selectedMfa === '4' || selectedMfa === '3') {
    const payloadData = { mfaType: mfaType.value };
    await getGuestLoginMfaRequest(payloadData);

    if (selectedMfa === '4') {
      store.state.guestLogin.mfaType = mfaType.value;
    }

    return;
  }

  if (selectedMfa === 'DMV-visit') {
    router.push({ name: 'GuestSignin' });
    return;
  }

  if (selectedMfa === 'MFA-unable') {
    router.push({ name: 'MultiFactAuth', query: { step: '6', from: '3' } });
  }
};
</script>


<style lang="scss" scoped>
.app-button-group{
  margin-top: 1rem;
  button {
    margin-bottom: 1.125em;
    &:not(:last-child) {
      margin-right: 1em;
    }
    &.disabled {
      cursor: not-allowed;
      opacity: 0.7;
    }
  }
}
.input-row {
  .user-mail-address-heading{
    font-weight: bold;
  }
}
.otp{
  position: absolute;
}
.otp-text{
  margin-left: 1rem;
  display: block;
  cursor: default !important;
  .user-address{
    line-height: 2rem;
    word-wrap: break-word; 
    span {
      display: block;
      line-height: 30px;
    }
  }
}
.error-msg{
  color: var(--ri-red);
}
</style>